import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { BinIcon, EditIcon, WarningIcon } from '@tom-ui/ui'
import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { YardBlockDto, YardBlockType } from '@yard/app/api'
import { getSlotCountByDestination } from '@yard/utils/yard-block.util'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { mapAllocationRuleDestinationToYardPositionDescriptor } from '../../helpers/allocation-rule.mapper'

interface Props {
  id: number
  minBay?: number
  maxBay?: number
  minRow?: number
  maxRow?: number
  index: number
  yardBlock: YardBlockDto
  yardBlocks: YardBlockDto[]
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}

export const DestinationItemOverview = observer((props: Props) => {
  const { id, minBay, maxBay, minRow, maxRow, index, yardBlocks, yardBlock, onEdit, onDelete } =
    props

  const { t } = useTranslate()

  const noSlotsAvailable =
    useMemo(
      () =>
        getSlotCountByDestination(
          yardBlocks,
          yardBlock.id,
          minBay ?? 0,
          maxBay ?? MAX_INT32_VALUE,
          minRow ?? 0,
          maxRow ?? MAX_INT32_VALUE,
        ),
      [yardBlocks, yardBlock.id, minBay, maxBay, minRow, maxRow],
    ) === 0

  return (
    <Draggable draggableId={id.toString()} index={index}>
      {provided => (
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'divider' }}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box {...provided.dragHandleProps} sx={{ display: 'flex', mr: 1, cursor: 'move' }}>
                <DragIndicatorIcon />
              </Box>
              <Stack direction='row' alignItems='center' gap={0.5}>
                {noSlotsAvailable && yardBlock.type !== YardBlockType.FlexibleBlock && (
                  <Tooltip title={t('noSlotsAvailable', 'No slots available for this destination')}>
                    <WarningIcon color='warning' fontSize='small' sx={{ mr: 0.5 }} />
                  </Tooltip>
                )}
                <Typography variant='subtitle1'>
                  {mapAllocationRuleDestinationToYardPositionDescriptor(
                    yardBlock,
                    minBay,
                    maxBay,
                    minRow,
                    maxRow,
                  )}
                </Typography>
              </Stack>
            </Box>
            <Box>
              <IconButton onClick={() => onEdit(id)} size='small'>
                <EditIcon fontSize='small' />
              </IconButton>
              <IconButton onClick={() => onDelete(id)} size='small'>
                <BinIcon fontSize='small' />
              </IconButton>
            </Box>
          </Stack>
        </Paper>
      )}
    </Draggable>
  )
})
